/**
 * Delivery country + language control and the consent-based delivery country suggestion.
 *
 * <delivery-localization> (sections/header.liquid): one panel with a searchable country list and
 * the store languages. It opens as a popover from the desktop header button and as a modal sheet
 * from the compact header button (below 990px) and the button in the mobile menu drawer. Saving
 * submits the Shopify localization form once, with both country_code and language_code.
 *
 * <country-suggestion> (layout/theme.liquid): suggests the country of a logged-in customer's
 * default address, otherwise the country Shopify detects for the visitor. It never switches by
 * itself (EU geo-blocking rules require explicit consent): the country only changes when the
 * visitor clicks "yes". A decision (yes, no, or a save in the panel) is remembered for 30 days.
 */
(() => {
  const DECISION_STORAGE_KEY = 'mv:delivery-country-decision';
  const DECISION_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;
  const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';

  const decisionStore = {
    /**
     * @returns {{choice: string, country: string, at: number} | null | undefined} The stored
     *   decision, null when there is none, undefined when storage is unavailable.
     */
    read() {
      let storedValue;
      try {
        storedValue = window.localStorage.getItem(DECISION_STORAGE_KEY);
      } catch (error) {
        return undefined;
      }

      if (!storedValue) return null;

      try {
        const decision = JSON.parse(storedValue);
        if (decision && typeof decision.at === 'number' && Date.now() - decision.at < DECISION_LIFETIME_MS) {
          return decision;
        }
      } catch (error) {
        // An unreadable value counts as no decision and is removed below.
      }

      this.clear();
      return null;
    },

    write(choice, country) {
      try {
        window.localStorage.setItem(DECISION_STORAGE_KEY, JSON.stringify({ choice, country, at: Date.now() }));
      } catch (error) {
        // Storage can be blocked (private mode, browser settings): the decision is then not remembered.
      }
    },

    clear() {
      try {
        window.localStorage.removeItem(DECISION_STORAGE_KEY);
      } catch (error) {
        // Nothing to clear when storage is unavailable.
      }
    },
  };

  const normalizeSearchText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const fillCountry = (template, countryName) => String(template || '').split('[country]').join(countryName);

  const runWhenIdle = (callback) => {
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => callback(), { timeout: 4000 });
      } else {
        window.setTimeout(callback, 1500);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  };

  class DeliveryLocalization extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.dialog = this.querySelector('dialog');
      this.form = this.dialog ? this.dialog.querySelector('form') : null;
      if (!this.form || typeof this.dialog.showModal !== 'function') return;

      this.isInitialized = true;
      this.searchInput = this.dialog.querySelector('[data-delivery-localization-search]');
      this.countryList = this.dialog.querySelector('[data-delivery-localization-countries]');
      this.emptyState = this.dialog.querySelector('[data-delivery-localization-empty]');
      this.note = this.dialog.querySelector('[data-note-template]');
      this.countryOptions = Array.from(this.dialog.querySelectorAll('[data-country-code]'));
      this.languageOptions = Array.from(this.dialog.querySelectorAll('[data-language-code]'));
      this.countryInput = this.form.querySelector('input[name="country_code"]');
      this.languageInput = this.form.querySelector('input[name="language_code"]');
      this.saveButton = this.form.querySelector('[type="submit"]');
      this.currentCountry = this.countryInput ? this.countryInput.value : '';
      this.currentLanguage = this.languageInput ? this.languageInput.value : '';
      this.triggers = Array.from(
        document.querySelectorAll(`[data-delivery-localization-trigger][aria-controls="${this.dialog.id}"]`)
      );
      this.desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
      this.searchTermsByOption = new Map();

      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onViewportChange = () => this.close({ restoreFocus: false });

      this.triggers.forEach((trigger) => trigger.addEventListener('click', () => this.toggle(trigger)));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      // The sheet's content fills the dialog, so a click on the dialog itself is a backdrop click.
      this.dialog.addEventListener('click', (event) => {
        if (this.isSheet && event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => this.syncExpandedState());
      this.dialog.addEventListener('focusout', (event) => this.onFocusOut(event));
      this.dialog
        .querySelectorAll('[data-delivery-localization-close]')
        .forEach((button) => button.addEventListener('click', () => this.close()));
      this.countryOptions.forEach((option) => option.addEventListener('click', () => this.selectCountry(option)));
      this.languageOptions.forEach((option) => option.addEventListener('click', () => this.selectLanguage(option)));

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => this.filterCountries());
        this.searchInput.addEventListener('keydown', (event) => this.onSearchKeydown(event));
      }

      if (this.countryList) {
        this.countryList.addEventListener('keydown', (event) => this.onCountryListKeydown(event));
      }

      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;

        this.endSubmitting();
        this.close({ restoreFocus: false });
      });
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      this.desktopMediaQuery.removeEventListener('change', this.onViewportChange);
    }

    toggle(trigger) {
      if (this.dialog.open) {
        this.close();
        return;
      }

      this.open(trigger);
    }

    open(trigger) {
      this.activeTrigger = trigger;
      this.isSheet = trigger.dataset.mode === 'sheet' || !this.desktopMediaQuery.matches;
      this.sortCountries();
      this.resetSelection();
      this.dialog.classList.toggle('delivery-localization__dialog--sheet', this.isSheet);

      if (this.isSheet) {
        this.dialog.showModal();
      } else {
        this.dialog.show();
        document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
      }

      this.desktopMediaQuery.addEventListener('change', this.onViewportChange);
      trigger.setAttribute('aria-expanded', 'true');
      this.scrollSelectedCountryIntoView();

      // The sheet takes focus itself, so a phone keyboard does not pop up over the list.
      if (this.isSheet || !this.searchInput) {
        this.dialog.focus();
      } else {
        this.searchInput.focus();
      }
    }

    close({ restoreFocus = true } = {}) {
      if (!this.dialog.open) return;

      this.dialog.close();
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      this.desktopMediaQuery.removeEventListener('change', this.onViewportChange);
      this.syncExpandedState();

      if (restoreFocus && this.activeTrigger && this.activeTrigger.isConnected) {
        this.activeTrigger.focus();
      }
    }

    syncExpandedState() {
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(this.dialog.open && trigger === this.activeTrigger));
      });
    }

    onKeydown(event) {
      // The modal sheet closes through the native cancel event instead.
      if (event.key !== 'Escape' || !this.dialog.open || this.isSheet) return;

      event.preventDefault();
      this.close();
    }

    onDocumentPointerDown(event) {
      const path = event.composedPath();
      if (path.includes(this.dialog) || (this.activeTrigger && path.includes(this.activeTrigger))) return;

      this.close({ restoreFocus: false });
    }

    onFocusOut(event) {
      if (this.isSheet || !this.dialog.open) return;

      const nextFocus = event.relatedTarget;
      if (!nextFocus || this.dialog.contains(nextFocus) || this.triggers.includes(nextFocus)) return;

      this.close({ restoreFocus: false });
    }

    resetSelection() {
      if (this.searchInput) this.searchInput.value = '';

      const currentCountryOption = this.countryOptions.find(
        (option) => option.dataset.countryCode === this.currentCountry
      );
      if (currentCountryOption) this.selectCountry(currentCountryOption);

      const currentLanguageOption = this.languageOptions.find(
        (option) => option.dataset.languageCode === this.currentLanguage
      );
      if (currentLanguageOption) this.selectLanguage(currentLanguageOption);

      this.filterCountries();
    }

    selectCountry(selectedOption) {
      this.countryOptions.forEach((option) => {
        option.setAttribute('aria-pressed', String(option === selectedOption));
      });

      if (this.countryInput) this.countryInput.value = selectedOption.dataset.countryCode;

      if (this.note) {
        const noteText = fillCountry(this.note.dataset.noteTemplate, selectedOption.dataset.countryName);
        if (this.note.textContent.trim() !== noteText) this.note.textContent = noteText;
      }

      this.updateCountryTabStop();
    }

    selectLanguage(selectedOption) {
      this.languageOptions.forEach((option) => {
        option.setAttribute('aria-pressed', String(option === selectedOption));
      });

      if (this.languageInput) this.languageInput.value = selectedOption.dataset.languageCode;
    }

    filterCountries() {
      if (!this.countryList) return;

      const query = normalizeSearchText(this.searchInput ? this.searchInput.value : '');
      let visibleCount = 0;

      this.countryOptions.forEach((option) => {
        const isMatch = query === '' || this.searchTermsFor(option).some((term) => term.includes(query));
        option.parentElement.hidden = !isMatch;
        if (isMatch) visibleCount += 1;
      });

      this.countryList.classList.toggle('is-filtered', query !== '');

      if (this.emptyState) {
        const emptyText = visibleCount === 0 ? this.emptyState.dataset.message : '';
        if (this.emptyState.textContent !== emptyText) this.emptyState.textContent = emptyText;
      }

      this.updateCountryTabStop();
    }

    /**
     * A country matches on the store's localized name, its ISO code and its name in each store
     * language, so "deu" finds Germany on the Dutch storefront as well.
     */
    searchTermsFor(option) {
      if (!this.searchTermsByOption.has(option)) {
        const countryCode = option.dataset.countryCode;
        const terms = new Set([option.dataset.countryName, countryCode]);

        this.regionNameFormatters().forEach((formatter) => {
          try {
            terms.add(formatter.of(countryCode));
          } catch (error) {
            // Unknown region for this locale: keep the store's own name.
          }
        });

        this.searchTermsByOption.set(option, Array.from(terms).filter(Boolean).map(normalizeSearchText));
      }

      return this.searchTermsByOption.get(option);
    }

    regionNameFormatters() {
      if (this.formatters) return this.formatters;

      this.formatters = [];
      if (typeof Intl !== 'object' || typeof Intl.DisplayNames !== 'function') return this.formatters;

      const locales = new Set([
        document.documentElement.lang,
        ...this.languageOptions.map((option) => option.dataset.languageCode),
      ]);

      locales.forEach((locale) => {
        if (!locale) return;

        try {
          this.formatters.push(new Intl.DisplayNames([locale], { type: 'region' }));
        } catch (error) {
          // Unsupported locale: skip it.
        }
      });

      return this.formatters;
    }

    visibleCountryOptions() {
      return this.countryOptions.filter((option) => !option.parentElement.hidden);
    }

    /**
     * The country list is one tab stop (roving tabindex): the selected country, or the first match.
     */
    updateCountryTabStop(focusTarget) {
      const visibleOptions = this.visibleCountryOptions();
      const tabStop =
        focusTarget ||
        visibleOptions.find((option) => option.getAttribute('aria-pressed') === 'true') ||
        visibleOptions[0];

      this.countryOptions.forEach((option) => {
        option.tabIndex = option === tabStop ? 0 : -1;
      });
    }

    focusCountryOption(option) {
      this.updateCountryTabStop(option);
      option.focus();
    }

    onSearchKeydown(event) {
      const firstVisibleOption = this.visibleCountryOptions()[0];

      if (event.key === 'ArrowDown' && firstVisibleOption) {
        event.preventDefault();
        this.focusCountryOption(firstVisibleOption);
      } else if (event.key === 'Enter') {
        // Enter picks the first match instead of submitting the form.
        event.preventDefault();
        if (firstVisibleOption && this.searchInput.value.trim() !== '') this.selectCountry(firstVisibleOption);
      }
    }

    onCountryListKeydown(event) {
      const visibleOptions = this.visibleCountryOptions();
      const currentIndex = visibleOptions.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      let nextOption;
      switch (event.key) {
        case 'ArrowDown':
          nextOption = visibleOptions[Math.min(currentIndex + 1, visibleOptions.length - 1)];
          break;
        case 'ArrowUp':
          if (currentIndex === 0 && this.searchInput) {
            event.preventDefault();
            this.searchInput.focus();
            return;
          }
          nextOption = visibleOptions[Math.max(currentIndex - 1, 0)];
          break;
        case 'Home':
          nextOption = visibleOptions[0];
          break;
        case 'End':
          nextOption = visibleOptions[visibleOptions.length - 1];
          break;
        default:
          return;
      }

      event.preventDefault();
      this.focusCountryOption(nextOption);
    }

    scrollSelectedCountryIntoView() {
      if (!this.countryList) return;

      const selectedOption = this.countryOptions.find((option) => option.getAttribute('aria-pressed') === 'true');
      if (!selectedOption) return;

      const item = selectedOption.parentElement;
      this.countryList.scrollTop = Math.max(0, item.offsetTop - (this.countryList.clientHeight - item.offsetHeight) / 2);
    }

    /**
     * Pinned countries (Netherlands, Belgium, Germany) stay on top; the rest is sorted by the
     * localized name with the storefront language's collation.
     */
    sortCountries() {
      if (this.hasSortedCountries || !this.countryList) return;

      this.hasSortedCountries = true;

      let collator;
      try {
        collator = new Intl.Collator(document.documentElement.lang || undefined, { sensitivity: 'base' });
      } catch (error) {
        return;
      }

      const nameOf = (item) => item.querySelector('[data-country-name]').dataset.countryName;
      Array.from(this.countryList.children)
        .filter((item) => !item.hasAttribute('data-pinned'))
        .sort((first, second) => collator.compare(nameOf(first), nameOf(second)))
        .forEach((item) => this.countryList.appendChild(item));

      this.countryOptions = Array.from(this.countryList.querySelectorAll('[data-country-code]'));
    }

    onSubmit(event) {
      if (this.isSubmitting) {
        event.preventDefault();
        return;
      }

      const countryChanged = Boolean(this.countryInput) && this.countryInput.value !== this.currentCountry;
      const languageChanged = Boolean(this.languageInput) && this.languageInput.value !== this.currentLanguage;

      if (!countryChanged && !languageChanged) {
        event.preventDefault();
        this.close();
        return;
      }

      this.isSubmitting = true;
      if (this.saveButton) this.saveButton.setAttribute('aria-busy', 'true');

      // A saved choice is an explicit decision: the suggestion bar stays away for 30 days.
      decisionStore.write('chosen', this.countryInput ? this.countryInput.value : this.currentCountry);
    }

    endSubmitting() {
      this.isSubmitting = false;
      if (this.saveButton) this.saveButton.removeAttribute('aria-busy');
    }
  }

  class CountrySuggestion extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      if (window.Shopify && window.Shopify.designMode) return;

      this.card = this.querySelector('[data-country-suggestion-card]');
      this.form = this.querySelector('form');
      this.message = this.querySelector('[data-country-suggestion-message]');
      this.acceptButton = this.querySelector('[data-country-suggestion-accept]');
      this.dismissButton = this.querySelector('[data-country-suggestion-dismiss]');
      this.status = this.querySelector('[data-country-suggestion-status]');
      this.countryInput = this.form ? this.form.querySelector('input[name="country_code"]') : null;
      if (!this.card || !this.message || !this.acceptButton || !this.dismissButton || !this.countryInput) return;

      this.countries = this.readCountries();
      this.currentCountry = this.dataset.currentCountry;

      const previewCountry = this.readPreviewCountry();
      if (previewCountry === 'RESET') decisionStore.clear();

      // A stored decision, or storage that cannot remember one, keeps the suggestion away.
      if (decisionStore.read() !== null) return;

      this.form.addEventListener('submit', (event) => this.onAccept(event));
      this.dismissButton.addEventListener('click', () => this.dismiss());
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;

        this.isSubmitting = false;
        this.acceptButton.removeAttribute('aria-busy');
        if (decisionStore.read() !== null) this.hide();
      });

      if (previewCountry && previewCountry !== 'RESET') {
        this.suggest(previewCountry);
      } else if (this.dataset.customerCountry) {
        this.suggest(this.dataset.customerCountry);
      } else {
        runWhenIdle(() => this.detectCountry());
      }
    }

    readCountries() {
      const source = this.querySelector('[data-country-suggestion-countries]');

      try {
        return JSON.parse(source ? source.textContent : '{}') || {};
      } catch (error) {
        return {};
      }
    }

    /**
     * QA hook for unpublished themes only: ?suggest_country=DE forces a suggestion without a VPN
     * and ?suggest_country=reset forgets a stored decision. Ignored on the published theme.
     */
    readPreviewCountry() {
      const theme = window.Shopify && window.Shopify.theme;
      if (!theme || theme.role === 'main') return null;

      const value = new URLSearchParams(window.location.search).get('suggest_country');
      return value ? value.trim().toUpperCase() : null;
    }

    async detectCountry() {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const query = new URLSearchParams({
        'country[enabled]': 'true',
        'country[exclude]': this.currentCountry || '',
        'language[enabled]': 'true',
        'language[exclude]': this.dataset.currentLanguage || '',
      });

      try {
        const response = await fetch(`${root}browsing_context_suggestions.json?${query}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const suggestions = await response.json();
        const detectedCountry = suggestions && suggestions.detected_values && suggestions.detected_values.country;
        if (detectedCountry && detectedCountry.handle) this.suggest(String(detectedCountry.handle).toUpperCase());
      } catch (error) {
        // The suggestion is optional: stay silent when the endpoint fails.
      }
    }

    suggest(countryCode) {
      const country = this.countries[countryCode];
      if (!country || countryCode === this.currentCountry) return;

      this.countryInput.value = countryCode;
      this.message.textContent = fillCountry(this.message.dataset.template, country.name);
      this.acceptButton.textContent = fillCountry(this.acceptButton.dataset.template, country.name);
      if (country.flag) this.renderFlag(country.flag);

      this.card.hidden = false;
      // Commit the hidden state first so the fade-in transition runs.
      void this.card.offsetWidth;
      this.card.classList.add('is-visible');

      if (this.status) this.status.textContent = this.message.textContent;
    }

    renderFlag(source) {
      const flag = document.createElement('img');
      flag.className = 'country-suggestion__flag';
      flag.src = source;
      flag.alt = '';
      flag.width = 20;
      flag.height = 15;
      this.message.before(flag);
    }

    onAccept(event) {
      if (this.isSubmitting) {
        event.preventDefault();
        return;
      }

      this.isSubmitting = true;
      this.acceptButton.setAttribute('aria-busy', 'true');
      decisionStore.write('accepted', this.countryInput.value);
    }

    dismiss() {
      decisionStore.write('dismissed', this.countryInput.value);
      this.hide();
    }

    hide() {
      const hadFocus = this.card.contains(document.activeElement);
      const hideCard = () => {
        this.card.hidden = true;
      };

      this.card.classList.remove('is-visible');
      this.card.addEventListener('transitionend', hideCard, { once: true });
      window.setTimeout(hideCard, 300);
      if (this.status) this.status.textContent = '';

      if (hadFocus) {
        const mainContent = document.getElementById('MainContent');
        if (mainContent) mainContent.focus({ preventScroll: true });
      }
    }
  }

  if (!customElements.get('delivery-localization')) {
    customElements.define('delivery-localization', DeliveryLocalization);
  }

  if (!customElements.get('country-suggestion')) {
    customElements.define('country-suggestion', CountrySuggestion);
  }
})();
